// Lib
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { Range } from '@tiptap/core';
import { Editor, posToDOMRect } from '@tiptap/react';
import { useSelector } from 'react-redux';

// Utils
import globalLogger from '../../logger';
import getParentScrollableSection from '../../utils/dom/getParentScrollableSection';
import { isPlatformPhoneOrMobileMode } from '../../platform/utils/platformDetailsUtils';
import {
    MentionSuggestion,
    mentionSuggestionToAttrs,
} from '../../../common/tiptap/extensions/mention/MentionSuggestion';
import {
    NoteInsertionSource,
    NoteInsertionType,
    trackNoteInsertionEvent,
} from '../../analytics/noteInsertionAnalyticsUtils';
import { PopupPosition } from './tiptapPopup';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Components
import { TiptapMentionsSuggesterPopup } from '../../element/card/tiptap/TiptapMentionsSuggesterPopup';

// Types
import { PlatformDetails } from '../../../common/platform/platformTypes';
import { MentionsSuggestionsManager } from '../../../common/tiptap/extensions/mention/SuggestionsManagerTypes';
import { InsertedPrefix } from '../../../common/tiptap/extensions/mention/TextMention';

type OnMentionCreated = (data: MentionSuggestion, popupInsertedPrefix: InsertedPrefix) => void;

type SuggestionCommand = (data: MentionSuggestion) => void;

type SuggestionCallbackProps = {
    editor: Editor;
    range: Range;
    props?: MentionSuggestion;
};
type RenderCallbackProps = SuggestionCallbackProps & {
    query: string;
    command: SuggestionCommand;
};

type SuggestionInitOptions = {
    // These are for the mentions plugin to talk to the outside world
    onMentionCreated: OnMentionCreated;
    onQueryUpdated: (query: string | null) => void;
    setPopupPosition: (position: PopupPosition) => void;
    onEnded: () => void;
    keyEvents: EventTarget;
    platformDetails: PlatformDetails;
};

type UseSuggestionsManagerOptions = {
    trackInsertions: boolean;
};

type MentionSession = {
    popupInsertedPrefix: InsertedPrefix;
    editor: Editor;
    range: Range;
};

const MOBILE_MENTIONS_POPUP_WIDTH = 320;
const MOBILE_MENTIONS_POPUP_MAX_HEIGHT = 175;

const logger = globalLogger.createChannel('suggest');

const cleanupToolbarInsertedMention = (session: MentionSession | null) => {
    if (session === null) return;
    if (session.popupInsertedPrefix === '') return;

    const insertedLeadingSpace = session.popupInsertedPrefix === ' @';
    const { from } = session.range;
    const to = session.editor.state.selection.$head.pos;
    const triggerFrom = insertedLeadingSpace && from > 0 ? from - 1 : from;
    const textWithPossibleSpace = session.editor.state.doc.textBetween(triggerFrom, to, '');

    if (insertedLeadingSpace && textWithPossibleSpace.startsWith(' @')) {
        session.editor.commands.deleteRange({ from: triggerFrom, to });
        return;
    }

    const exactText = session.editor.state.doc.textBetween(from, to, '');
    if (!exactText.startsWith('@')) return;

    session.editor.commands.deleteRange({ from, to });
};

const getMentionsPopupPosition = (editor: Editor, range: Range, platformDetails: PlatformDetails): PopupPosition => {
    const rangeRect = posToDOMRect(editor.view, range.from, range.from);
    const point = { x: rangeRect.x, y: rangeRect.y + rangeRect.height };

    if (!isPlatformPhoneOrMobileMode(platformDetails)) return { anchor: 'bottom', point };

    const scrollableElement = getParentScrollableSection(editor.options.element as HTMLElement);
    if (!scrollableElement) return { anchor: 'bottom', point };

    const scrollableElementRect = scrollableElement.getBoundingClientRect();
    const scrollableElementScrollExcessPadding =
        // @ts-expect-error - value not defined in CSSStyleValue
        scrollableElement.computedStyleMap().get('padding-bottom')?.value || 0;
    let anchor: PopupPosition['anchor'] = 'bottom';

    // On mobile, update position.x such that the initial popup position should be centered on the screen
    point.x = (scrollableElementRect.width - MOBILE_MENTIONS_POPUP_WIDTH) / 2;

    // On mobile, update position.y such that the initial popup position should not be hidden by the keyboard
    if (point.y + MOBILE_MENTIONS_POPUP_MAX_HEIGHT > window.innerHeight - scrollableElementScrollExcessPadding) {
        point.y = rangeRect.top;
        anchor = 'top';
    }

    return { anchor, point };
};

/**
 * This object serves as a bridge between the main Milanote application state and the internal state of the
 * Tiptap editor. The Mention extension expects an object matching the SuggestionOptions spec as part of its
 * configuration.
 *
 * To help simplify the API, this isn't exposed directly; it should be used via the useSuggestionManager hook below.
 */
const suggestions = ({
    onMentionCreated,
    onQueryUpdated,
    onEnded,
    setPopupPosition,
    keyEvents,
    platformDetails,
}: SuggestionInitOptions): MentionsSuggestionsManager => {
    let storedCommand: SuggestionCommand | null = null;
    let currentQuery: string | null = '';
    let currentSelection: MentionSuggestion | null = null;
    let session: MentionSession | null = null;

    const setQuery = (q: string | null) => {
        currentQuery = q;
        onQueryUpdated?.(q);
    };

    const complete = () => {
        if (!currentSelection) return;

        storedCommand?.(currentSelection);
    };

    const hidePopup = () => {
        setQuery(null);
        currentSelection = null;
    };

    return {
        complete: () => {
            complete();
        },

        setSelection: (data: MentionSuggestion) => {
            // Sensitive data audit: SAFE — debug logs are only visible in development environments and not sent remotely
            logger.debug('SELECTION', { mentionSuggestion: data });
            currentSelection = data;
        },

        command: ({ editor, range, props: suggestion }: SuggestionCallbackProps) => {
            // Sensitive data audit: SAFE — debug logs are only visible in development environments and not sent remotely
            logger.debug('COMMAND', { range, suggestion });

            if (!suggestion || session === null) return;

            // The end point of the provided range is sometimes inaccurate and will leave
            // stray characters in the text. Using the current cursor position is more reliable.
            // (the provided start position is still accurate)
            const mentionRange = {
                from: range.from,
                to: editor.state.selection.$head.pos,
            };

            const { mentionKey, userId } = mentionSuggestionToAttrs(suggestion);
            const text = suggestion.name;

            const success = editor.chain().createMention(mentionRange, text, userId, mentionKey).focus().run();
            if (!success) return;

            onMentionCreated?.(suggestion, session.popupInsertedPrefix);
        },

        render: () => {
            // This is called when the editor component mounts, not when a lookup begins.
            // Subsequent @'s in the same editor will reuse this same object.

            return {
                onStart: ({ editor, range, command }: RenderCallbackProps) => {
                    // Sensitive data audit: SAFE — debug logs are only visible in development environments and not sent remotely
                    logger.debug('START', range);

                    const popupInsertedPrefix = (editor.storage.textMention.popupInsertedPrefix ??
                        '') as InsertedPrefix;
                    storedCommand = command;
                    session = {
                        editor,
                        range,
                        popupInsertedPrefix,
                    };

                    editor.storage.textMention.popupInsertedPrefix = '';

                    setQuery('');
                    const position = getMentionsPopupPosition(editor, range, platformDetails);
                    setPopupPosition(position);

                    // hide the popup when the user clicks away without completing a mention
                    editor.on('blur', hidePopup);
                },

                onUpdate: (props: RenderCallbackProps) => {
                    // Sensitive data audit: SAFE — debug logs are only visible in development environments and not sent remotely
                    logger.debug('UPDATE', props, props.range);
                    if (session === null) return;

                    const { query, editor, range } = props;

                    session.range = range;

                    setQuery(query);

                    const position = getMentionsPopupPosition(editor, range, platformDetails);
                    setPopupPosition(position);
                },

                onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                    // This function's return value indicates whether the keypress should be
                    // handled by the editor as well (ie, should it type the character?)

                    // If no active query, always follow normal editor behaviour
                    if (currentQuery === null) return false;

                    // Sensitive data audit: SAFE — Key name only
                    logger.debug('KEYDOWN', event.key);

                    if (['Enter', 'Tab'].includes(event.key)) {
                        complete();

                        // when completing a mention with no suggestion, close the popup and
                        // handle the keypress too (ie insert a newline / tab character)
                        if (!currentSelection) return false;

                        return true;
                    }

                    if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
                        // If no current selection, let editor handle it
                        if (!currentSelection) return false;

                        // dispatch as a new event; the EventTarget api doesn't like double-handling
                        keyEvents.dispatchEvent(new KeyboardEvent('keydown', { key: event.key }));
                        return true;
                    }

                    if (event.key === 'Escape') {
                        // (currently fully deselects the element)
                        // Sensitive data audit: SAFE — Static escape key event with no user content
                        logger.debug('ESCAPE');

                        cleanupToolbarInsertedMention(session);

                        // If no current selection, hand control back to editor
                        if (!currentSelection) return false;

                        setQuery(null);
                        event.preventDefault();
                        event.stopPropagation();
                        return true;
                    }

                    return false;
                },

                onExit({ editor }: RenderCallbackProps) {
                    // Sensitive data audit: SAFE — Static exit event with no user content
                    logger.debug('EXIT');

                    // clear our local state
                    storedCommand = null;
                    session = null;

                    setQuery(null);
                    onEnded?.();

                    editor.off('blur', hidePopup);
                },
            };
        },
    };
};

/**
 * This hook provides a simplified API for the Mention extension's suggestion system.
 * It returns a tuple containing the suggestion manager object to pass to Mention.configure
 * and a ReactNode to render the suggestion popup.
 */
export const useSuggestionsManager = (
    { trackInsertions }: UseSuggestionsManagerOptions = { trackInsertions: false },
): [MentionsSuggestionsManager, ReactNode] => {
    const [currentQuery, setCurrentQuery] = useState<string | null>(null);
    const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    // The key events will be coming from inside the Tiptap editor, and we want to dispatch
    // the responses to those keypresses *back* into it. Using an EventTarget lets us avoid
    // having to store refs to elements or complex callback chains.
    const keyEvents = useMemo(() => new EventTarget(), []);

    const suggestion = useMemo(
        () =>
            suggestions({
                onMentionCreated: (mention, popupInsertedPrefix) => {
                    // Sensitive data audit: SAFE — debug logs are only visible in development environments and not sent remotely
                    logger.debug('CREATE', { mention, popupInsertedPrefix });
                    setCurrentQuery(null);
                    setPopupPosition(null);
                    if (!trackInsertions) return;

                    trackNoteInsertionEvent(
                        NoteInsertionType.MENTION,
                        popupInsertedPrefix ? NoteInsertionSource.INSERT_MENU : NoteInsertionSource.INLINE,
                    );
                },
                onQueryUpdated: (q) => {
                    setCurrentQuery(q);
                },
                onEnded: () => {
                    setCurrentQuery(null);
                    setPopupPosition(null);
                },
                setPopupPosition,
                keyEvents,
                platformDetails,
            }),
        [keyEvents, platformDetails],
    );

    const onSelectUser = useCallback(
        (userDetails: MentionSuggestion) => {
            suggestion.setSelection(userDetails);
            suggestion.complete();
        },
        [suggestion],
    );

    const onPreviewUserChanged = useCallback(
        (userDetails: MentionSuggestion) => {
            suggestion.setSelection(userDetails);
        },
        [suggestion],
    );

    const mentionsPopup = useMemo(
        () =>
            currentQuery !== null ? (
                <TiptapMentionsSuggesterPopup
                    currentSuggestionQuery={currentQuery}
                    position={popupPosition}
                    onSelectUser={onSelectUser}
                    onPreviewUserChanged={onPreviewUserChanged}
                    keyEvents={keyEvents}
                />
            ) : null,
        [currentQuery, popupPosition, onSelectUser],
    );

    return [suggestion, mentionsPopup];
};
